import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUpgrade } from "@/context/UpgradeContext";
import { Loader2, Send, Sparkles, DollarSign, Briefcase, Compass, Heart, Lock, BookOpen, Users, Brain, ClipboardList } from "lucide-react";
import { CoachMemoryDrawer } from "@/components/app/CoachMemoryDrawer";
import { CoachPlanPanel } from "@/components/app/CoachPlanPanel";

const COACHES = [
  { id: "general", label: "General", desc: "Life & goals", icon: Sparkles, gated: false },
  { id: "financial", label: "Financial", desc: "Money & insurance", icon: DollarSign, gated: true },
  { id: "business", label: "Business", desc: "Startup & tax strategy", icon: Briefcase, gated: true },
  { id: "career", label: "Career", desc: "Growth & positioning", icon: Compass, gated: true },
  { id: "health", label: "Wellness", desc: "Health & habits", icon: Heart, gated: true },
  { id: "parenting", label: "Parenting", desc: "Kids & family life", icon: BookOpen, gated: true },
  { id: "relationships", label: "Relationships", desc: "Connection & communication", icon: Users, gated: true },
  { id: "mental_health", label: "Mental Wellness", desc: "Practices & support", icon: Brain, gated: true },
];

const DISCLAIMER = {
  financial: "Educational only — verify specifics with a licensed advisor.",
  business: "Educational only — confirm legal/tax specifics with a professional.",
  career: "Educational only — coaching, not professional placement advice.",
  health: "Educational only — not medical advice.",
  parenting: "Warm guidance, not clinical advice. See a pediatrician or family therapist for anything safety-related.",
  relationships: "Educational only — for ongoing distress, please see a licensed couples or family therapist.",
  mental_health: "Wellness education, NOT therapy. In crisis? Call your local crisis line or reach a licensed clinician right away.",
};

export default function Coach() {
  const { user } = useAuth();
  const { openUpgrade } = useUpgrade();
  const [coach, setCoach] = useState("general");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showMemory, setShowMemory] = useState(false);
  const [showPlan, setShowPlan] = useState(false);
  const [planRefreshKey, setPlanRefreshKey] = useState(0);
  const [autoGeneratePlan, setAutoGeneratePlan] = useState(false);
  const endRef = useRef(null);

  const specializedAllowed = user?.plan && ["pro", "admin"].includes(user.plan);
  const supportsPlan = coach !== "general";

  const loadMessages = async (c) => {
    setLoading(true);
    try {
      const { data } = await api.get("/coach/messages", { params: { coach: c } });
      setMessages(data.messages || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { loadMessages(coach); setAutoGeneratePlan(false); }, [coach]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

  const pickCoach = (c) => {
    const meta = COACHES.find((x) => x.id === c);
    if (meta?.gated && !specializedAllowed) {
      openUpgrade?.({ title: "Unlock specialized coaches",
        message: "Financial, Business, Career and Wellness coaches are a Pro feature.",
        plan: "pro" });
      return;
    }
    setCoach(c);
  };

  const send = async () => {
    const msg = input.trim();
    if (!msg) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg, id: Date.now(), coach }]);
    setSending(true);
    try {
      const { data } = await api.post("/coach/message", { message: msg, coach });
      setMessages((m) => [...m, { role: "assistant", content: data.reply, id: Date.now() + 1, coach }]);
      if (data.build_plan) {
        toast.success("Great — I have what I need. Building your plan now…");
        setShowPlan(true);
        setAutoGeneratePlan(true);
        setPlanRefreshKey((k) => k + 1);
      }
    } catch (e) {
      !e.handledUpgrade && toast.error(apiError(e.response?.data?.detail));
    } finally { setSending(false); }
  };

  const active = COACHES.find((c) => c.id === coach);
  const ActiveIcon = active?.icon || Sparkles;

  return (
    <div className="flex min-h-[calc(100vh-8rem)] flex-col lg:min-h-[calc(100vh-6rem)]" data-testid="coach-page">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-sunrise text-[#2D3748]"><ActiveIcon className="h-5 w-5" /></div>
          <div>
            <h1 className="font-nunito text-xl font-bold tracking-tight text-[#2D3748]">
              {active?.label} Coach
            </h1>
            <p className="text-xs text-[#718096]">Knows your goals, habits & progress</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {supportsPlan && (
            <button onClick={() => setShowPlan((s) => !s)} data-testid="open-plan-btn"
              className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 font-nunito text-sm font-bold ${
                showPlan ? "border-[#FF7E67] bg-[#FFE8E3] text-[#C2410C]" : "border-[#FFE8E3] bg-white text-[#C2410C] hover:bg-[#FFF9F5]"
              }`}>
              <ClipboardList className="h-4 w-4" strokeWidth={2.5} /> Plan
            </button>
          )}
          <button onClick={() => setShowMemory(true)} data-testid="open-memory-btn"
            className="inline-flex items-center gap-2 rounded-full border border-[#FFE8E3] bg-white px-4 py-2 font-nunito text-sm font-bold text-[#C2410C] hover:bg-[#FFF9F5]">
            <Brain className="h-4 w-4" strokeWidth={2.5} /> Memory
          </button>
        </div>
      </div>

      {/* Coach selector */}
      <div className="mb-4 flex flex-wrap gap-2" data-testid="coach-selector">
        {COACHES.map((c) => {
          const Icon = c.icon;
          const isActive = coach === c.id;
          const locked = c.gated && !specializedAllowed;
          return (
            <button key={c.id} onClick={() => pickCoach(c.id)}
              data-testid={`coach-tab-${c.id}`}
              className={`flex items-center gap-2 rounded-full border px-3.5 py-1.5 font-nunito text-xs font-bold transition-all ${
                isActive
                  ? "border-[#FF7E67] bg-[#FFE8E3] text-[#C2410C] scale-105"
                  : "border-[#FFE8E3] bg-white text-[#718096] hover:border-[#FF7E67]"
              }`} title={c.desc}>
              <Icon className="h-3.5 w-3.5" strokeWidth={2.5} />
              {c.label}
              {locked && <Lock className="h-3 w-3" />}
            </button>
          );
        })}
      </div>

      {active?.gated && DISCLAIMER[active.id] && (
        <div className="mb-3 rounded-2xl border border-[#FFE8E3] bg-[#FFF9F5] px-4 py-2 font-quicksand text-xs text-[#718096]" data-testid="coach-disclaimer">
          {DISCLAIMER[active.id]}
        </div>
      )}

      {supportsPlan && showPlan && (
        <div className="mb-4" data-testid="coach-plan-section">
          <CoachPlanPanel coach={coach} coachLabel={active?.label}
            refreshKey={planRefreshKey} autoGenerate={autoGeneratePlan} />
        </div>
      )}

      <div className="flex-1 space-y-4 overflow-y-auto rounded-3xl border border-[#FFE8E3] bg-white p-5">
        {loading ? (
          <div className="flex h-full items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#FF7E67]" /></div>
        ) : messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center text-center text-[#718096]">
            <ActiveIcon className="mb-3 h-8 w-8 text-[#FF7E67]" />
            <p>Ask me anything about {active?.desc.toLowerCase()}.</p>
          </div>
        ) : messages.map((m) => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm ${m.role === "user" ? "bg-sunrise text-[#2D3748]" : "border border-[#FFE8E3] bg-[#FFF9F5] text-[#2D3748]"}`}>
              {m.content}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start"><div className="rounded-2xl border border-[#FFE8E3] bg-[#FFF9F5] px-4 py-3"><Loader2 className="h-4 w-4 animate-spin text-[#FF7E67]" /></div></div>
        )}
        <div ref={endRef} />
      </div>

      <div className="mt-4 flex gap-3">
        <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder={`Message your ${active?.label.toLowerCase()} coach…`} data-testid="coach-input"
          className="h-13 flex-1 rounded-full border border-[#FFE8E3] bg-[#FFF9F5] px-5 py-3.5 text-[#2D3748] outline-none focus:border-[#FF7E67]" />
        <button onClick={send} disabled={sending} data-testid="coach-send-btn"
          className="flex h-13 w-13 items-center justify-center rounded-full bg-sunrise p-3.5 text-[#2D3748] transition-all hover:scale-105 disabled:opacity-70">
          <Send className="h-5 w-5" />
        </button>
      </div>

      {showMemory && (
        <CoachMemoryDrawer coach={coach} coachLabel={`${active?.label} Coach`}
          onClose={() => setShowMemory(false)} />
      )}
    </div>
  );
}
